import { useMemo, useCallback, useState } from 'react';
import { Button, Spinner } from '@heroui/react';
import { Save, CheckCircle, ChevronDown, ChevronRight } from 'lucide-react';
import type { Markup, DrawingPage } from '@estimator/shared';
import { useBulkUpdateMarkups, useDeleteMarkup, useUpdateMarkup } from '@/api/markups';
import { useAreas, useCreateArea } from '@/api/areas';
import { useItemCategories } from '@/api/item-categories';
import { useMarkupStore, type OverrideValues } from '@/store/markupStore';
import { useIsAdmin, usePermission } from '@/hooks/usePermission';
import { useSubmitRevision, useApproveRevision, useRequestRevise } from '@/api/drawing-sets';
import MarkupCard from './MarkupCard';

interface ReviewPanelProps {
  projectId: string;
  markups: Markup[];
  currentPageId?: string | null;
  pages?: DrawingPage[];
  onNavigateToPage?: (pageId: string) => void;
  revision?: {
    id: string;
    status: string;
    revision_number: number;
    review_notes?: string | null;
  } | null;
}

export default function ReviewPanel({ projectId, markups: allMarkups, currentPageId, pages = [], onNavigateToPage, revision }: ReviewPanelProps) {
  const [markupScope, setMarkupScope] = useState<'page' | 'all'>('page');

  const markups = useMemo(() => {
    if (markupScope === 'all' || !currentPageId) return allMarkups;
    return allMarkups.filter((m) => m.page_id === currentPageId || m.page_id === null);
  }, [allMarkups, currentPageId, markupScope]);

  const getPageName = useCallback((pageId: string | null) => {
    if (!pageId) return undefined;
    const page = pages.find((p) => p.id === pageId);
    if (!page) return undefined;
    return `Page ${page.page_number}`;
  }, [pages]);
  const {
    selectedMarkupId,
    pendingOverrides,
    isDirty,
    selectMarkup,
    hoverMarkup,
    setOverride,
    clearOverride,
    clearAllOverrides,
  } = useMarkupStore();

  const isAdmin = useIsAdmin();
  const canSubmit = usePermission('projects.drawing-sets.submit') || isAdmin;

  const submitRevision = useSubmitRevision();
  const approveRevision = useApproveRevision();
  const requestRevise = useRequestRevise();
  const [reviewNotes, setReviewNotes] = useState('');
  const [showNotesInput, setShowNotesInput] = useState(false);

  const bulkUpdate = useBulkUpdateMarkups(projectId);
  const deleteMarkup = useDeleteMarkup();
  const updateMarkup = useUpdateMarkup();
  const createArea = useCreateArea(projectId);

  const { data: areas = [] } = useAreas(projectId);
  const { data: categories = [] } = useItemCategories(true);
  const [viewMode, setViewMode] = useState<'area' | 'type' | 'flat'>('area');
  const [collapsedGroups, setCollapsedGroups] = useState<Set<string>>(new Set());

  const toggleGroup = useCallback((key: string) => {
    setCollapsedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }, []);

  const groupedMarkups = useMemo(() => {
    if (viewMode === 'flat') return null;

    const groups: Record<string, { label: string; color: string; markups: typeof markups }> = {};

    if (viewMode === 'area') {
      for (const m of markups) {
        const areaId = m.area_id ?? '__unassigned';
        if (!groups[areaId]) {
          const area = areas.find((a: any) => a.id === areaId);
          groups[areaId] = {
            label: area?.name ?? 'Unassigned',
            color: areaId === '__unassigned' ? 'var(--text-muted)' : 'var(--accent)',
            markups: [],
          };
        }
        groups[areaId].markups.push(m);
      }
    } else {
      for (const m of markups) {
        const catId = (m as any).item_category_id ?? '__uncategorized';
        if (!groups[catId]) {
          const cat = categories.find((c: any) => c.id === catId);
          groups[catId] = {
            label: cat?.name ?? 'Uncategorized',
            color: cat?.color ?? 'var(--text-muted)',
            markups: [],
          };
        }
        groups[catId].markups.push(m);
      }
    }

    return groups;
  }, [markups, areas, categories, viewMode]);

  // Calculate tallies
  const tally = useMemo(() => {
    let totalArea = 0;
    let overriddenCount = 0;

    markups.forEach((m) => {
      const pending = pendingOverrides.get(m.id);
      const effectiveArea = pending?.area ?? m.override_area ?? m.ai_area ?? 0;
      totalArea += effectiveArea;

      if (pending?.area != null || m.override_area != null) {
        overriddenCount++;
      }
    });

    return {
      totalArea,
      overriddenCount,
      markupCount: markups.length,
    };
  }, [markups, pendingOverrides]);

  const handleSaveAll = useCallback(async () => {
    if (pendingOverrides.size === 0) return;

    const items = Array.from(pendingOverrides.entries()).map(([id, overrides]) => ({
      id,
      override_area: overrides.area ?? undefined,
      override_length: overrides.length ?? undefined,
      override_width: overrides.width ?? undefined,
      override_height: overrides.height ?? undefined,
      override_notes: overrides.notes ?? undefined,
    }));

    try {
      await bulkUpdate.mutateAsync({ markups: items });
      clearAllOverrides();
    } catch {
      // Error handled by TanStack Query
    }
  }, [pendingOverrides, bulkUpdate, clearAllOverrides]);

  return (
    <div
      className="flex flex-col h-full"
      style={{ backgroundColor: 'var(--bg-surface)' }}
    >
      {/* Header */}
      <div
        className="flex-shrink-0 px-4 py-3 border-b"
        style={{ borderColor: 'var(--border)' }}
      >
        <div className="flex items-center justify-between">
          <h2
            className="text-sm font-semibold"
            style={{ color: 'var(--text-primary)' }}
          >
            Markup Review
          </h2>
          <div className="flex gap-0.5">
            {(['area', 'type', 'flat'] as const).map((mode) => (
              <button
                key={mode}
                onClick={() => setViewMode(mode)}
                className="px-2 py-0.5 rounded text-[10px] font-medium transition-colors"
                style={{
                  backgroundColor: viewMode === mode ? 'color-mix(in srgb, var(--accent) 15%, transparent)' : 'transparent',
                  color: viewMode === mode ? 'var(--accent)' : 'var(--text-muted)',
                }}
              >
                {mode === 'area' ? 'By Area' : mode === 'type' ? 'By Type' : 'Flat'}
              </button>
            ))}
          </div>
        </div>
        <div className="flex items-center justify-between mt-1">
          <p className="text-[10px]" style={{ color: 'var(--text-muted)' }}>
            {tally.markupCount} markups{' '}
            {tally.overriddenCount > 0 && (
              <span style={{ color: 'var(--blue)' }}>
                &middot; {tally.overriddenCount} overridden
              </span>
            )}
          </p>
          <div className="flex gap-0.5">
            {(['page', 'all'] as const).map((scope) => (
              <button
                key={scope}
                onClick={() => setMarkupScope(scope)}
                className="px-2 py-0.5 rounded text-[10px] font-medium transition-colors"
                style={{
                  backgroundColor: markupScope === scope ? 'color-mix(in srgb, var(--blue, #60a5fa) 15%, transparent)' : 'transparent',
                  color: markupScope === scope ? 'var(--blue, #60a5fa)' : 'var(--text-muted)',
                }}
              >
                {scope === 'page' ? 'This Page' : 'All Pages'}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Scrollable card list */}
      <div className="flex-1 overflow-y-auto px-3 py-2 space-y-2">
        {markups.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 gap-2">
            <p className="text-xs" style={{ color: 'var(--text-muted)' }}>
              No markups on this page.
            </p>
          </div>
        ) : viewMode === 'flat' || !groupedMarkups ? (
          markups.map((markup, index) => (
            <MarkupCard
              key={markup.id}
              markup={markup}
              index={index}
              isSelected={markup.id === selectedMarkupId}
              pendingOverride={pendingOverrides.get(markup.id) ?? null}
              onOverrideChange={(field, value) => setOverride(markup.id, field, value)}
              onOverrideClear={() => clearOverride(markup.id)}
              onSelect={() => selectMarkup(markup.id === selectedMarkupId ? null : markup.id)}
              onHover={(hovering) => hoverMarkup(hovering ? markup.id : null)}
              onDelete={() => {
                if (markup.id === selectedMarkupId) selectMarkup(null);
                clearOverride(markup.id);
                deleteMarkup.mutate({ id: markup.id, projectId });
              }}
              isDeleting={deleteMarkup.isPending && deleteMarkup.variables?.id === markup.id}
              areas={areas}
              onAreaChange={(areaId) => updateMarkup.mutate({ id: markup.id, projectId, area_id: areaId })}
              onLabelChange={(label) => updateMarkup.mutate({ id: markup.id, projectId, label })}
              onNavigateToPage={onNavigateToPage}
              pageName={getPageName(markup.page_id)}
              onCreateArea={(name) => createArea.mutate({ name }, {
                onSuccess: (newArea) => updateMarkup.mutate({ id: markup.id, projectId, area_id: newArea.id }),
              })}
            />
          ))
        ) : (
          Object.entries(groupedMarkups).map(([key, group]) => {
            const isCollapsed = collapsedGroups.has(key);
            return (
              <div key={key}>
                <button
                  onClick={() => toggleGroup(key)}
                  className="flex items-center gap-2 w-full px-2 py-1.5 rounded-md text-xs font-medium transition-colors hover:brightness-110"
                  style={{
                    backgroundColor: `color-mix(in srgb, ${group.color} 8%, transparent)`,
                    color: 'var(--text-primary)',
                  }}
                >
                  {isCollapsed ? <ChevronRight size={12} /> : <ChevronDown size={12} />}
                  <span
                    className="w-2.5 h-2.5 rounded-sm flex-shrink-0"
                    style={{ backgroundColor: group.color }}
                  />
                  {group.label}
                  <span className="ml-auto text-[10px]" style={{ color: 'var(--text-muted)' }}>
                    {group.markups.length}
                  </span>
                </button>
                {!isCollapsed && (
                  <div className="space-y-1.5 mt-1.5 ml-2">
                    {group.markups.map((markup, index) => (
                      <MarkupCard
                        key={markup.id}
                        markup={markup}
                        index={index}
                        isSelected={markup.id === selectedMarkupId}
                        pendingOverride={pendingOverrides.get(markup.id) ?? null}
                        onOverrideChange={(field, value) => setOverride(markup.id, field, value)}
                        onOverrideClear={() => clearOverride(markup.id)}
                        onSelect={() => selectMarkup(markup.id === selectedMarkupId ? null : markup.id)}
                        onHover={(hovering) => hoverMarkup(hovering ? markup.id : null)}
                        onDelete={() => {
                          if (markup.id === selectedMarkupId) selectMarkup(null);
                          clearOverride(markup.id);
                          deleteMarkup.mutate({ id: markup.id, projectId });
                        }}
                        isDeleting={deleteMarkup.isPending && deleteMarkup.variables?.id === markup.id}
                        areas={areas}
                        onAreaChange={(areaId) => updateMarkup.mutate({ id: markup.id, projectId, area_id: areaId })}
                        onLabelChange={(label) => updateMarkup.mutate({ id: markup.id, projectId, label })}
                        onNavigateToPage={onNavigateToPage}
                        pageName={getPageName(markup.page_id)}
                        onCreateArea={(name) => createArea.mutate({ name }, {
                          onSuccess: (newArea) => updateMarkup.mutate({ id: markup.id, projectId, area_id: newArea.id }),
                        })}
                      />
                    ))}
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>

      {/* Footer tally + Save button */}
      <div
        className="flex-shrink-0 border-t px-4 py-3 space-y-2.5"
        style={{
          borderColor: 'var(--border)',
          backgroundColor: 'var(--bg-panel)',
        }}
      >
        {/* Tally */}
        <div className="flex items-center justify-between">
          <div className="flex flex-col gap-0.5">
            <div className="flex items-center gap-2">
              <span className="text-[10px] uppercase tracking-wider" style={{ color: 'var(--text-muted)' }}>
                Total Floor Area
              </span>
              <span className="font-ui text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>
                {tally.totalArea.toFixed(2)} m&sup2;
              </span>
            </div>
            <span className="text-[10px]" style={{ color: 'var(--text-muted)' }}>
              {tally.markupCount} markups &middot; {tally.overriddenCount} overridden
            </span>
          </div>
        </div>

        {/* Save button */}
        <Button
          fullWidth
          size="sm"
          isDisabled={!isDirty || bulkUpdate.isPending}
          onPress={handleSaveAll}
          startContent={
            bulkUpdate.isPending ? (
              <Spinner size="sm" color="current" />
            ) : bulkUpdate.isSuccess ? (
              <CheckCircle size={14} />
            ) : (
              <Save size={14} />
            )
          }
          className="h-9 font-medium text-xs transition-all duration-200"
          style={{
            backgroundColor: isDirty ? 'var(--accent)' : 'var(--bg-hover)',
            color: isDirty ? '#0f1012' : 'var(--text-muted)',
            boxShadow: isDirty ? '0 0 12px rgba(232, 160, 32, 0.3)' : 'none',
            animation: isDirty ? 'pulse-glow 2s ease-in-out infinite' : 'none',
          }}
        >
          {bulkUpdate.isPending
            ? 'Saving...'
            : bulkUpdate.isSuccess && !isDirty
              ? `Saved \u00B7 ${tally.overriddenCount} values overridden`
              : 'Save All to DB'}
        </Button>

        {bulkUpdate.isError && (
          <p className="text-[10px] text-center" style={{ color: 'var(--red)' }}>
            Failed to save. Please try again.
          </p>
        )}

        {/* Approval actions — only shown when viewing a revision */}
        {revision && (
          <div className="space-y-2 mt-2">
            {/* Engineer actions */}
            {canSubmit && (revision.status === 'DRAFT' || revision.status === 'REVISE') && (
              <Button
                fullWidth
                size="sm"
                color="primary"
                className="h-9 font-medium text-xs"
                onPress={() => submitRevision.mutate(revision.id)}
                isLoading={submitRevision.isPending}
              >
                {revision.status === 'REVISE' ? 'Resubmit for Review' : 'Submit for Review'}
              </Button>
            )}

            {/* Reviewer actions */}
            {isAdmin && (revision.status === 'SUBMITTED' || revision.status === 'IN_REVIEW') && (
              <>
                <Button
                  fullWidth
                  size="sm"
                  className="h-9 font-medium text-xs"
                  style={{ backgroundColor: 'var(--green)', color: '#0f1012' }}
                  onPress={() => approveRevision.mutate(revision.id)}
                  isLoading={approveRevision.isPending}
                >
                  Approve
                </Button>

                {showNotesInput ? (
                  <div className="space-y-1.5">
                    <textarea
                      value={reviewNotes}
                      onChange={(e) => setReviewNotes(e.target.value)}
                      placeholder="Describe what needs to change..."
                      className="w-full text-xs p-2 rounded border resize-none"
                      style={{
                        borderColor: 'var(--border)',
                        backgroundColor: 'var(--bg-base)',
                        color: 'var(--text-primary)',
                      }}
                      rows={3}
                      autoFocus
                    />
                    <Button
                      fullWidth
                      size="sm"
                      className="h-9 font-medium text-xs"
                      style={{ backgroundColor: 'var(--yellow)', color: '#0f1012' }}
                      onPress={() => {
                        requestRevise.mutate({ revisionId: revision.id, reviewNotes });
                        setShowNotesInput(false);
                        setReviewNotes('');
                      }}
                      isDisabled={!reviewNotes.trim()}
                      isLoading={requestRevise.isPending}
                    >
                      Send Revision Request
                    </Button>
                  </div>
                ) : (
                  <Button
                    fullWidth
                    size="sm"
                    variant="bordered"
                    className="h-9 font-medium text-xs"
                    style={{ borderColor: 'var(--yellow)', color: 'var(--yellow)' }}
                    onPress={() => setShowNotesInput(true)}
                  >
                    Request Changes
                  </Button>
                )}
              </>
            )}

            {/* Review notes from previous round */}
            {revision.review_notes && revision.status === 'REVISE' && (
              <div
                className="text-xs p-2 rounded border"
                style={{ borderColor: 'var(--yellow)', backgroundColor: 'color-mix(in srgb, var(--yellow) 5%, transparent)' }}
              >
                <p className="font-medium mb-1" style={{ color: 'var(--yellow)' }}>Revision Notes:</p>
                <p style={{ color: 'var(--text-secondary)' }}>{revision.review_notes}</p>
              </div>
            )}

            {/* Status indicator for read-only states */}
            {revision.status === 'APPROVED' && (
              <div className="text-center py-2">
                <span className="text-xs font-medium" style={{ color: 'var(--green)' }}>
                  &#10003; This revision is approved
                </span>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Keyframe for the amber pulse glow */}
      <style>{`
        @keyframes pulse-glow {
          0%, 100% { box-shadow: 0 0 12px rgba(232, 160, 32, 0.3); }
          50% { box-shadow: 0 0 20px rgba(232, 160, 32, 0.5); }
        }
      `}</style>
    </div>
  );
}
